import React, { useState, useRef, useEffect } from 'react';
import { useModal } from '../context/ModalContext';
import { FiMove, FiRotateCw, FiMinus, FiPlus, FiSquare, FiCircle, FiX, FiSave, FiEdit2 } from 'react-icons/fi';

const CHAIR_SIZE = 20;
const CHAIR_GAP = 14;

const getChairAngleTowardsTable = (chairCenterX, chairCenterY, tableCenterX, tableCenterY) => {
  const angleToCenterDeg = Math.atan2(tableCenterY - chairCenterY, tableCenterX - chairCenterX) * (180 / Math.PI);
  // Calibrate for chair art where base orientation is "facing up"
  return (angleToCenterDeg + 90) % 360;
};

// Calculate chair positions around a table based on shape and size
const calculateChairPositions = (table) => {
  const chairCount = table.chair_count || table.capacity || 4;
  const width = table.width || 100;
  const height = table.height || 100;
  const shape = table.shape || 'rectangle';
  const rotation = (table.rotation || 0) * (Math.PI / 180); // Convert to radians
  const centerX = (table.position_x || 0) + width / 2;
  const centerY = (table.position_y || 0) + height / 2;
  const chairSize = CHAIR_SIZE;
  const spacing = CHAIR_GAP;
  
  const positions = [];
  
  if (shape === 'circle' || shape === 'oval') {
    // Circular/Oval arrangement - chairs evenly distributed around ellipse perimeter
    const radiusX = width / 2 + spacing + (chairSize / 2);
    const radiusY = height / 2 + spacing + (chairSize / 2);
    
    // Distribute chairs evenly around the circle
    for (let i = 0; i < chairCount; i++) {
      // Calculate angle: evenly distribute 360 degrees, starting from top (-90 degrees)
      const angleRad = (2 * Math.PI * i) / chairCount - (Math.PI / 2);
      
      // Calculate chair center position on ellipse
      const chairCenterX = centerX + radiusX * Math.cos(angleRad);
      const chairCenterY = centerY + radiusY * Math.sin(angleRad);
      
      // Position chair so its center is at the calculated position
      const x = chairCenterX - (chairSize / 2);
      const y = chairCenterY - (chairSize / 2);
      
      // Make chair point toward table center
      const chairAngleDeg = getChairAngleTowardsTable(chairCenterX, chairCenterY, centerX, centerY);
      
      positions.push({ 
        x, 
        y, 
        angle: chairAngleDeg 
      });
    }
  } else {
    // Rectangular/square arrangement - chairs face inward toward table
    const perimeter = 2 * (width + height);
    const chairsPerSide = Math.ceil(chairCount / 4);
    let chairIndex = 0;
    
    // Top side - chairs face down (0 degrees)
    for (let i = 0; i < chairsPerSide && chairIndex < chairCount; i++) {
      const x = (table.position_x || 0) + (width / (chairsPerSide + 1)) * (i + 1) - chairSize / 2;
      const y = (table.position_y || 0) - spacing - chairSize;
      const chairCenterX = x + chairSize / 2;
      const chairCenterY = y + chairSize / 2;
      positions.push({ x, y, angle: getChairAngleTowardsTable(chairCenterX, chairCenterY, centerX, centerY) });
      chairIndex++;
    }
    
    // Right side - chairs face left (90 degrees)
    for (let i = 0; i < chairsPerSide && chairIndex < chairCount; i++) {
      const x = (table.position_x || 0) + width + spacing;
      const y = (table.position_y || 0) + (height / (chairsPerSide + 1)) * (i + 1) - chairSize / 2;
      const chairCenterX = x + chairSize / 2;
      const chairCenterY = y + chairSize / 2;
      positions.push({ x, y, angle: getChairAngleTowardsTable(chairCenterX, chairCenterY, centerX, centerY) });
      chairIndex++;
    }
    
    // Bottom side - chairs face up (180 degrees)
    for (let i = 0; i < chairsPerSide && chairIndex < chairCount; i++) {
      const x = (table.position_x || 0) + (width / (chairsPerSide + 1)) * (i + 1) - chairSize / 2;
      const y = (table.position_y || 0) + height + spacing;
      const chairCenterX = x + chairSize / 2;
      const chairCenterY = y + chairSize / 2;
      positions.push({ x, y, angle: getChairAngleTowardsTable(chairCenterX, chairCenterY, centerX, centerY) });
      chairIndex++;
    }
    
    // Left side - chairs face right (270 degrees)
    for (let i = 0; i < chairsPerSide && chairIndex < chairCount; i++) {
      const x = (table.position_x || 0) - spacing - chairSize;
      const y = (table.position_y || 0) + (height / (chairsPerSide + 1)) * (i + 1) - chairSize / 2;
      const chairCenterX = x + chairSize / 2;
      const chairCenterY = y + chairSize / 2;
      positions.push({ x, y, angle: getChairAngleTowardsTable(chairCenterX, chairCenterY, centerX, centerY) });
      chairIndex++;
    }
  }
  
  // Apply rotation to all chairs
  return positions.map(pos => {
    const chairCenterX = pos.x + chairSize / 2;
    const chairCenterY = pos.y + chairSize / 2;
    const dx = chairCenterX - centerX;
    const dy = chairCenterY - centerY;
    const rotatedCenterX = centerX + dx * Math.cos(rotation) - dy * Math.sin(rotation);
    const rotatedCenterY = centerY + dx * Math.sin(rotation) + dy * Math.cos(rotation);
    return {
      ...pos,
      x: rotatedCenterX - chairSize / 2,
      y: rotatedCenterY - chairSize / 2,
      angle: pos.angle + (table.rotation || 0)
    };
  });
};

function TableLayoutEditor({ tables, onSave, onTableUpdate, floor = 1, locationFilter = '' }) {
  const { alert } = useModal();
  const [selectedTable, setSelectedTable] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [isRotating, setIsRotating] = useState(false);
  const [rotateStart, setRotateStart] = useState({ angle: 0, centerX: 0, centerY: 0 });
  const [scale, setScale] = useState(1);
  const containerRef = useRef(null);
  const [editMode, setEditMode] = useState(false);
  const [tempChanges, setTempChanges] = useState({});
  const [panMode, setPanMode] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });

  // Filter tables by floor and location
  const floorTables = tables.filter(t => {
    const matchesFloor = (t.layout_floor || 1) === floor;
    const tableLocation = t.location || 'Indoor';
    const matchesLocation = locationFilter ? tableLocation === locationFilter : true;
    return matchesFloor && matchesLocation;
  });

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left) / scale;
      const y = (e.clientY - rect.top) / scale;

      if (isPanning) {
        const deltaX = e.clientX - panStart.x;
        const deltaY = e.clientY - panStart.y;
        setPanOffset({
          x: panStart.offsetX + deltaX,
          y: panStart.offsetY + deltaY
        });
        return;
      }

      if (isDragging && selectedTable) {
        const newX = Math.max(0, Math.min(x - dragOffset.x, 2000 - (selectedTable.width || 100)));
        const newY = Math.max(0, Math.min(y - dragOffset.y, 2000 - (selectedTable.height || 100)));
        
        setTempChanges(prev => ({
          ...prev,
          [selectedTable.id]: {
            ...prev[selectedTable.id],
            position_x: newX,
            position_y: newY
          }
        }));
      } else if (isResizing && selectedTable) {
        const deltaX = x - resizeStart.x;
        const deltaY = y - resizeStart.y;
        const newWidth = Math.max(50, resizeStart.width + deltaX);
        const newHeight = Math.max(50, resizeStart.height + deltaY);
        
        setTempChanges(prev => ({
          ...prev,
          [selectedTable.id]: {
            ...prev[selectedTable.id],
            width: newWidth,
            height: newHeight
          }
        }));
      } else if (isRotating && selectedTable) {
        const centerX = (selectedTable.position_x || 0) + (selectedTable.width || 100) / 2;
        const centerY = (selectedTable.position_y || 0) + (selectedTable.height || 100) / 2;
        const angle = Math.atan2(y - centerY, x - centerX) * (180 / Math.PI);
        const normalizedAngle = ((angle - rotateStart.angle + 360) % 360);
        
        setTempChanges(prev => ({
          ...prev,
          [selectedTable.id]: {
            ...prev[selectedTable.id],
            rotation: normalizedAngle
          }
        }));
      }
    };

    const handleMouseUp = () => {
      if (isDragging || isResizing || isRotating || isPanning) {
        setIsDragging(false);
        setIsResizing(false);
        setIsRotating(false);
        setIsPanning(false);
      }
    };

    if (isDragging || isResizing || isRotating || isPanning) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, isRotating, isPanning, selectedTable, dragOffset, resizeStart, rotateStart, scale, panStart]);

  const handleCanvasMouseDown = (e) => {
    if (!panMode || editMode) return;
    setIsPanning(true);
    setPanStart({
      x: e.clientX,
      y: e.clientY,
      offsetX: panOffset.x,
      offsetY: panOffset.y
    });
  };

  const handleTableMouseDown = (e, table) => {
    if (!editMode) return;
    e.stopPropagation();
    const currentTable = { ...table, ...tempChanges[table.id] };
    setSelectedTable(currentTable);
    const rect = containerRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / scale;
    const y = (e.clientY - rect.top) / scale;
    
    setDragOffset({
      x: x - (currentTable.position_x || 0),
      y: y - (currentTable.position_y || 0)
    });
    setIsDragging(true);
  };

  const handleResizeMouseDown = (e, table) => {
    if (!editMode) return;
    e.stopPropagation();
    const currentTable = { ...table, ...tempChanges[table.id] };
    setSelectedTable(currentTable);
    const rect = containerRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / scale;
    const y = (e.clientY - rect.top) / scale;
    
    setResizeStart({
      x,
      y,
      width: currentTable.width || 100,
      height: currentTable.height || 100
    });
    setIsResizing(true);
  };

  const handleRotateMouseDown = (e, table) => {
    if (!editMode) return;
    e.stopPropagation();
    const currentTable = { ...table, ...tempChanges[table.id] };
    setSelectedTable(currentTable);
    const rect = containerRef.current.getBoundingClientRect();
    const centerX = (currentTable.position_x || 0) + (currentTable.width || 100) / 2;
    const centerY = (currentTable.position_y || 0) + (currentTable.height || 100) / 2;
    const x = (e.clientX - rect.left) / scale;
    const y = (e.clientY - rect.top) / scale;
    const startAngle = Math.atan2(y - centerY, x - centerX) * (180 / Math.PI);
    
    setRotateStart({
      angle: startAngle,
      centerX,
      centerY
    });
    setIsRotating(true);
  };

  const getTableStyle = (table) => {
    const changes = tempChanges[table.id] || {};
    const x = changes.position_x !== undefined ? changes.position_x : (table.position_x || 0);
    const y = changes.position_y !== undefined ? changes.position_y : (table.position_y || 0);
    const width = changes.width !== undefined ? changes.width : (table.width || 100);
    const height = changes.height !== undefined ? changes.height : (table.height || 100);
    const rotation = changes.rotation !== undefined ? changes.rotation : (table.rotation || 0);
    const shape = changes.shape !== undefined ? changes.shape : (table.shape || 'rectangle');

    const isSelected = selectedTable?.id === table.id;
    const statusColor = {
      available: '#10b981',
      occupied: '#ef4444',
      reserved: '#f59e0b',
      cleaning: '#3b82f6'
    }[table.status] || '#6b7280';

    return {
      position: 'absolute',
      left: `${x * scale}px`,
      top: `${y * scale}px`,
      width: `${width * scale}px`,
      height: `${height * scale}px`,
      transform: `rotate(${rotation}deg)`,
      transformOrigin: 'center center',
      cursor: editMode ? 'move' : 'pointer',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      transition: isDragging || isResizing || isRotating ? 'none' : 'all 0.2s',
      zIndex: isSelected ? 10 : 1,
    };
  };

  const handleSave = async () => {
    const updates = Object.keys(tempChanges).map(id => {
      const table = tables.find(t => t.id === id);
      const changes = tempChanges[id];
      return {
        id,
        ...changes,
        // Ensure all required fields are included
        position_x: changes.position_x !== undefined ? changes.position_x : (table?.position_x || 0),
        position_y: changes.position_y !== undefined ? changes.position_y : (table?.position_y || 0),
        width: changes.width !== undefined ? changes.width : (table?.width || 100),
        height: changes.height !== undefined ? changes.height : (table?.height || 100),
        rotation: changes.rotation !== undefined ? changes.rotation : (table?.rotation || 0),
        chair_count: changes.chair_count !== undefined ? changes.chair_count : (table?.chair_count || table?.capacity || 4),
        shape: changes.shape !== undefined ? changes.shape : (table?.shape || 'rectangle')
      };
    });
    
    if (updates.length > 0) {
      try {
        await onSave(updates);
        setTempChanges({});
        setEditMode(false);
        setSelectedTable(null);
      } catch (error) {
        console.error('Save error:', error);
        await alert('Failed to save layout. Please try again.', 'Error', 'error');
      }
    }
  };

  const handleTableClick = (table) => {
    if (!editMode) {
      setSelectedTable(table);
    }
  };

  const updateTableProperty = (tableId, property, value) => {
    setTempChanges(prev => ({
      ...prev,
      [tableId]: {
        ...prev[tableId],
        [property]: value
      }
    }));
  };

  return (
    <div className="relative w-full h-full bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border-2 border-gray-300 overflow-hidden">
      {/* Toolbar */}
      <div className="absolute top-4 left-4 right-4 z-20 bg-white rounded-lg shadow-lg p-3 flex items-center gap-3 flex-wrap">
        <button
          onClick={() => {
            setEditMode(!editMode);
            if (editMode) {
              setTempChanges({});
              setSelectedTable(null);
            }
          }}
          className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center gap-2 ${
            editMode 
              ? 'bg-blue-600 text-white hover:bg-blue-700' 
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          <FiEdit2 /> {editMode ? 'Editing' : 'Edit Layout'}
        </button>

        <button
          onClick={() => setPanMode(!panMode)}
          className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center gap-2 ${
            panMode
              ? 'bg-gray-900 text-white hover:bg-gray-800'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
          title="Pan canvas"
        >
          <FiMove /> {panMode ? 'Panning' : 'Pan'}
        </button>
        
        {/* Zoom controls - available in both normal and edit mode */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Zoom:</span>
          <button
            onClick={() => setScale(Math.max(0.5, scale - 0.1))}
            className="p-1.5 bg-gray-200 hover:bg-gray-300 rounded"
          >
            <FiMinus />
          </button>
          <span className="text-sm font-semibold w-12 text-center">{Math.round(scale * 100)}%</span>
          <button
            onClick={() => setScale(Math.min(2, scale + 0.1))}
            className="p-1.5 bg-gray-200 hover:bg-gray-300 rounded"
          >
            <FiPlus />
          </button>
        </div>

        <div className="flex items-center gap-1">
          <span className="text-sm text-gray-600">Move:</span>
          <button
            onClick={() => setPanOffset(prev => ({ ...prev, x: prev.x - 40 }))}
            className="px-2 py-1 text-sm bg-gray-200 hover:bg-gray-300 rounded"
            title="Move left"
          >
            ◀
          </button>
          <button
            onClick={() => setPanOffset(prev => ({ ...prev, y: prev.y - 40 }))}
            className="px-2 py-1 text-sm bg-gray-200 hover:bg-gray-300 rounded"
            title="Move up"
          >
            ▲
          </button>
          <button
            onClick={() => setPanOffset(prev => ({ ...prev, y: prev.y + 40 }))}
            className="px-2 py-1 text-sm bg-gray-200 hover:bg-gray-300 rounded"
            title="Move down"
          >
            ▼
          </button>
          <button
            onClick={() => setPanOffset(prev => ({ ...prev, x: prev.x + 40 }))}
            className="px-2 py-1 text-sm bg-gray-200 hover:bg-gray-300 rounded"
            title="Move right"
          >
            ▶
          </button>
        </div>
        
        {editMode && (
          <>
            {Object.keys(tempChanges).length > 0 && (
              <button
                onClick={handleSave}
                className="ml-auto px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold flex items-center gap-2"
              >
                <FiSave /> Save Changes
              </button>
            )}
          </>
        )}
        
        {selectedTable && (
          <div className="ml-auto flex items-center gap-2 text-sm">
            <span className="font-semibold">Table {selectedTable.table_number}</span>
            {editMode && (
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={tempChanges[selectedTable.id]?.chair_count !== undefined 
                    ? tempChanges[selectedTable.id].chair_count 
                    : (selectedTable.chair_count || selectedTable.capacity)}
                  onChange={(e) => updateTableProperty(selectedTable.id, 'chair_count', parseInt(e.target.value) || 0)}
                  className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                  placeholder="Chairs"
                  min="0"
                />
                <select
                  value={tempChanges[selectedTable.id]?.shape || selectedTable.shape || 'rectangle'}
                  onChange={(e) => updateTableProperty(selectedTable.id, 'shape', e.target.value)}
                  className="px-2 py-1 border border-gray-300 rounded text-sm"
                >
                  <option value="rectangle">Rectangle</option>
                  <option value="square">Square</option>
                  <option value="circle">Circle</option>
                  <option value="oval">Oval</option>
                </select>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Layout Canvas */}
      <div
        ref={containerRef}
        className={`relative w-full h-full ${panMode && !editMode ? (isPanning ? 'cursor-grabbing' : 'cursor-grab') : ''}`}
        style={{ 
          minHeight: '600px',
          height: 'calc(100vh - 300px)',
          transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${scale})`,
          transformOrigin: 'top left'
        }}
        onMouseDown={handleCanvasMouseDown}
        onClick={() => !editMode && setSelectedTable(null)}
      >
        {/* Grid Background */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `
              linear-gradient(to right, #cbd5e1 1px, transparent 1px),
              linear-gradient(to bottom, #cbd5e1 1px, transparent 1px)
            `,
            backgroundSize: `${50 * scale}px ${50 * scale}px`
          }}
        />

        {/* Tables with Chairs */}
        {floorTables.map((table) => {
          const changes = tempChanges[table.id] || {};
          const currentTable = { ...table, ...changes };
          const chairPositions = calculateChairPositions(currentTable);
          const isSelected = selectedTable?.id === table.id;
          const statusColor = {
            available: '#10b981',
            occupied: '#ef4444',
            reserved: '#f59e0b',
            cleaning: '#3b82f6'
          }[table.status] || '#6b7280';
          
          return (
            <React.Fragment key={table.id}>
              {/* Chairs */}
              {chairPositions.map((chair, idx) => (
                <div
                  key={`chair-${table.id}-${idx}`}
                  className="absolute"
                  style={{
                    left: `${chair.x * scale}px`,
                    top: `${chair.y * scale}px`,
                    width: `${CHAIR_SIZE * scale}px`,
                    height: `${CHAIR_SIZE * scale}px`,
                    transform: `rotate(${chair.angle}deg)`,
                    transformOrigin: 'center center',
                    zIndex: selectedTable?.id === table.id ? 9 : 0,
                    opacity: 0.95
                  }}
                >
                  {/* Chair Visual - clean top-down shape for consistent circular layout */}
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      position: 'relative'
                    }}
                  >
                    {/* Seat */}
                    <div
                      style={{
                        position: 'absolute',
                        bottom: `${2 * scale}px`,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: `${14 * scale}px`,
                        height: `${11 * scale}px`,
                        background: 'linear-gradient(180deg, #6b7280 0%, #4b5563 100%)',
                        border: `${1 * scale}px solid #374151`,
                        borderRadius: `${3 * scale}px`,
                        boxShadow: `
                          0 ${1 * scale}px ${2 * scale}px rgba(0,0,0,0.35),
                          inset 0 ${0.5 * scale}px ${1 * scale}px rgba(255,255,255,0.15)
                        `
                      }}
                    />
                    
                    {/* Backrest */}
                    <div
                      style={{
                        position: 'absolute',
                        top: `${1 * scale}px`,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: `${12 * scale}px`,
                        height: `${7 * scale}px`,
                        background: 'linear-gradient(180deg, #6b7280 0%, #4b5563 100%)',
                        border: `${1 * scale}px solid #374151`,
                        borderRadius: `${3 * scale}px ${3 * scale}px ${2 * scale}px ${2 * scale}px`,
                        boxShadow: `
                          0 ${1 * scale}px ${2 * scale}px rgba(0,0,0,0.3),
                          inset 0 ${0.5 * scale}px ${1 * scale}px rgba(255,255,255,0.12)
                        `
                      }}
                    />
                  </div>
                </div>
              ))}
              
              {/* Table */}
              <div
                style={getTableStyle(currentTable)}
                onMouseDown={(e) => handleTableMouseDown(e, currentTable)}
                onClick={() => handleTableClick(currentTable)}
              >
                {/* Table Top - Dark solid color appearance */}
                <div
                  style={{
                    position: 'absolute',
                    inset: '0',
                    borderRadius: currentTable.shape === 'circle' || currentTable.shape === 'oval' ? '50%' : '8px',
                    backgroundColor: '#6b7280',
                    border: 'none',
                    boxShadow: `
                      inset 0 2px 4px rgba(0,0,0,0.3),
                      0 4px 8px rgba(0,0,0,0.2)
                    `,
                    zIndex: 1
                  }}
                />
                
                {/* Table Content */}
                <div className="text-center p-2 relative z-10" style={{ pointerEvents: 'none' }}>
                  <div className="font-bold text-sm text-white drop-shadow-lg" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                    {table.table_number}
                  </div>
                  <div className="text-xs text-white opacity-90 drop-shadow" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                    {currentTable.chair_count !== undefined ? currentTable.chair_count : (table.chair_count || table.capacity)} seats
                  </div>
                  {table.status !== 'available' && (
                    <div className="text-xs font-semibold mt-1 text-white drop-shadow" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                      {table.status}
                    </div>
                  )}
                </div>

                {/* Resize Handle */}
                {editMode && selectedTable?.id === table.id && (
                  <>
                    <div
                      className="absolute bottom-0 right-0 w-4 h-4 bg-blue-600 rounded-full cursor-nwse-resize border-2 border-white z-20"
                      onMouseDown={(e) => handleResizeMouseDown(e, currentTable)}
                      style={{ transform: 'translate(50%, 50%)' }}
                    />
                    <div
                      className="absolute top-0 right-0 w-4 h-4 bg-green-600 rounded-full cursor-grab border-2 border-white z-20"
                      onMouseDown={(e) => handleRotateMouseDown(e, currentTable)}
                      style={{ transform: 'translate(50%, -50%)' }}
                    />
                  </>
                )}
              </div>
            </React.Fragment>
          );
        })}

        {floorTables.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center text-gray-400">
            <div className="text-center">
              <p className="text-lg font-semibold mb-2">No tables on this floor</p>
              <p className="text-sm">Add tables and arrange them in the layout</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TableLayoutEditor;
